/**
 * HTTP Client 封装 - Axios + Token 自动续期拦截器
 *
 * 增强版：增加网络异常分类、全局错误上下文广播、自动重试机制
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import type { ApiResponse } from '@/services/types'
import { ApiError, PERSONAL_SPACE_ERROR_CODES } from '@/utils/apiError'
import { API_BASE_URL } from '@/utils/apiBaseUrl'
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isTokenExpiringSoon,
  shouldRefreshAccessToken,
} from '@/utils/tokenManager'

// ==================== 错误分类 ====================

/** HTTP 错误类别 */
export type ErrorClassify = 'network' | 'timeout' | 'server' | 'client' | 'unauthorized' | 'unknown'

/** 分类化错误信息 */
export interface ClassifiedError {
  /** 错误类别 */
  category: ErrorClassify
  /** 用户可见的友好消息 */
  userMessage: string
  /** 原始错误对象（调试用） */
  original: unknown
  /** HTTP 状态码（如有） */
  status?: number
  /** 业务错误码（如有） */
  errorCode?: number
  /** 是否应该触发全局错误横幅 */
  shouldShowGlobalAlert: boolean
}

/**
 * 429 限流响应（如概览侧 DashboardRateLimitFilter）：归为 client 不弹全局横幅，
 * 但文案必须说清是「请求太频繁」。修复系统概览审查#9 —— 它此前落进通用 client 分支，
 * 管理员看到的是「请求参数有误」，与真实原因无关，重试建议也跟着错。
 */
function rateLimited(original: unknown, status: number): ClassifiedError {
  return {
    category: 'client',
    userMessage: '请求过于频繁，请稍后再试',
    original,
    status,
    errorCode: status,
    shouldShowGlobalAlert: false,
  }
}

/**
 * 从 HTTP 失败响应体中提取后端 message。
 *
 * 三条链路的 body 形态不同，必须在这里收敛：
 *   - axios：JSON 已解析成对象（{code,message}）
 *   - el-upload（XMLHttpRequest）：只有原始字符串，可能是 JSON 文本
 *   - nginx 等反代直回：整页 HTML（如 413 Request Entity Too Large），没有任何可用文案
 * 此前只读 (body as any)?.message，HTML 字符串取不到值，413 只能退化成一串状态码甚至整页 HTML。
 */
export function extractBackendMessage(body: unknown): string | undefined {
  if (body == null) return undefined
  if (typeof body === 'string') {
    const text = body.trim()
    if (!text.startsWith('{')) return undefined // HTML 错误页 / 纯文本
    try {
      return extractBackendMessage(JSON.parse(text))
    } catch {
      return undefined
    }
  }
  if (typeof body === 'object') {
    const message = (body as { message?: unknown }).message
    return typeof message === 'string' && message.trim() ? message : undefined
  }
  return undefined
}

/** 413 优先于状态码文案：请求在 nginx（client_max_body_size）或后端 multipart 处被拦下，语义只有「文件过大」 */
const PAYLOAD_TOO_LARGE_MESSAGE = '文件过大，超出服务器允许的上传大小'

/**
 * 解析一次 HTTP 失败的用户文案：后端 message 优先，其次按状态码兜底。
 * axios 与 el-upload 两条上传链路共用同一份口径。
 */
export function resolveHttpErrorMessage(
  status: number | undefined,
  body: unknown,
  fallback: string,
): string {
  return extractBackendMessage(body) ?? (status === 413 ? PAYLOAD_TOO_LARGE_MESSAGE : fallback)
}

/**
 * 对 AxiosError / ApiError / 普通 Error 进行分类，返回结构化错误信息。
 *
 * 分类优先级：
 *   1. ApiError（业务码）→ 按 code 区间判断
 *   2. AxiosError → 按 HTTP status 判断
 *   3. 普通 Error → 按 message 关键词判断
 *   4. 兜底 unknown
 */
export function classifyError(error: unknown): ClassifiedError {
  // 1. ApiError（业务码）
  if (error instanceof ApiError) {
    const code = error.code
    if (code === 401) {
      return {
        category: 'unauthorized',
        userMessage: '登录状态已过期，请重新登录',
        original: error,
        status: 401,
        errorCode: code,
        shouldShowGlobalAlert: false, // 401 走自动登出流程，不需要全局横幅
      }
    }
    if (code === 429) {
      return rateLimited(error, code)
    }
    if (code >= 500) {
      return {
        category: 'server',
        userMessage: `服务器错误（${code}），请稍后重试`,
        original: error,
        status: code,
        errorCode: code,
        shouldShowGlobalAlert: true,
      }
    }
    if (code >= 400 && code < 500) {
      return {
        category: 'client',
        userMessage: error.message || '请求参数有误',
        original: error,
        status: code,
        errorCode: code,
        shouldShowGlobalAlert: false,
      }
    }
    // 0/-1 表示网络/HTTP 层错误
    return classifyByMessage(error.message || '请求失败', error)
  }

  // 2. AxiosError（HTTP 层错误）
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosErr = error as AxiosError
    const status = axiosErr.response?.status

    // 有 response 但 code !== 200 → 走业务码判断
    if (status) {
      if (status === 401) {
        return {
          category: 'unauthorized',
          userMessage: '登录状态已过期，请重新登录',
          original: error,
          status,
          shouldShowGlobalAlert: false,
        }
      }
      if (status === 429) {
        return rateLimited(error, status)
      }
      if (status >= 500) {
        return {
          category: 'server',
          userMessage: resolveHttpErrorMessage(
            status,
            axiosErr.response?.data,
            `服务器错误（${status}），请稍后重试`,
          ),
          original: error,
          status,
          errorCode: status,
          shouldShowGlobalAlert: true,
        }
      }
      if (status >= 400) {
        return {
          category: 'client',
          userMessage: resolveHttpErrorMessage(
            status,
            axiosErr.response?.data,
            `请求错误（${status}）`,
          ),
          original: error,
          status,
          errorCode: status,
          shouldShowGlobalAlert: false,
        }
      }
    }

    // 无 response → 网络层面问题
    if (axiosErr.code === 'ECONNABORTED' || axiosErr.message?.includes('timeout')) {
      return {
        category: 'timeout',
        userMessage: '请求超时，请检查网络后重试',
        original: error,
        shouldShowGlobalAlert: true,
      }
    }
    if (axiosErr.code === 'ERR_NETWORK' || axiosErr.code === 'ENOTFOUND' ||
        axiosErr.code === 'ECONNREFUSED' || axiosErr.code === 'ERR_BAD_REQUEST' ||
        axiosErr.message?.includes('Network Error') || axiosErr.message?.includes('failed to connect')) {
      return {
        category: 'network',
        userMessage: '网络连接异常，请检查您的网络设置',
        original: error,
        shouldShowGlobalAlert: true,
      }
    }
    // 其他 axios 错误（如 CORS）
    return {
      category: 'network',
      userMessage: '网络通信异常，请稍后重试',
      original: error,
      shouldShowGlobalAlert: true,
    }
  }

  // 3. 普通 Error
  if (error instanceof Error) {
    return classifyByMessage(error.message, error)
  }

  // 4. 兜底
  return {
    category: 'unknown',
    userMessage: '发生未知错误，请稍后重试',
    original: error,
    shouldShowGlobalAlert: true,
  }
}

/** 基于消息关键词的分类兜底 */
function classifyByMessage(message: string, original: unknown): ClassifiedError {
  const text = message.toLowerCase()
  if (text.includes('timeout') || text.includes('timed out') || text.includes('超时')) {
    return {
      category: 'timeout',
      userMessage: '请求超时，请检查网络后重试',
      original,
      shouldShowGlobalAlert: true,
    }
  }
  if (text.includes('network') || text.includes('failed to fetch') || text.includes('net::err') ||
      text.includes('connection') || text.includes('refused') || text.includes('unreachable')) {
    return {
      category: 'network',
      userMessage: '网络连接异常，请检查您的网络设置',
      original,
      shouldShowGlobalAlert: true,
    }
  }
  if (text.includes('500') || text.includes('internal server')) {
    return {
      category: 'server',
      userMessage: '服务器内部错误，请稍后重试',
      original,
      status: 500,
      errorCode: 500,
      shouldShowGlobalAlert: true,
    }
  }
  return {
    category: 'unknown',
    userMessage: '发生未知错误，请稍后重试',
    original,
    shouldShowGlobalAlert: true,
  }
}

// ==================== 简易指数退避重试 ====================

const MAX_RETRIES = 1
const RETRY_DELAY_MS = 1500

/**
 * 对可重试的请求执行最多 MAX_RETRIES+1 次尝试。
 * 仅 network / timeout 类错误会触发重试。
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
): Promise<T> {
  let lastError: unknown
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await fn()
    } catch (e) {
      const classified = classifyError(e)
      lastError = e
      if (!classified.shouldShowGlobalAlert || attempt >= MAX_RETRIES) {
        throw e
      }
      // 短暂等待后重试（指数退避）
      const delay = RETRY_DELAY_MS * Math.pow(2, attempt)
      await new Promise((r) => setTimeout(r, delay))
    }
  }
  throw lastError
}

// ==================== 原有逻辑 ====================
// API_BASE_URL 真源见 @/utils/apiBaseUrl（已去尾部斜杠，避免拼出 "//api/xxx" 协议相对 URL）

// 统一的 Token 刷新 Promise，避免并发刷新竞态
let refreshPromise: Promise<string> | null = null

/** 执行 Token 刷新（被动 401 或主动预刷新共用） */
function executeRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise

  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token'))
  }

  // 网络/超时类失败给一次退避重试：刷新请求本身幂等（同一 refreshToken 换发新 token），
  // 重放安全。withRetry 只对 shouldShowGlobalAlert 的分类重试，401/403 认证失败照旧立即失败。
  refreshPromise = withRetry(() =>
    axios.post(
      `${API_BASE_URL}/api/auth/refresh`,
      { refreshToken },
      { timeout: 10000 },
    ),
  )
    .then((response) => {
      const { accessToken, refreshToken: newRefreshToken, expiresIn } =
        response.data.data
      setTokens(accessToken, newRefreshToken, expiresIn)
      return accessToken
    })
    .catch((error: AxiosError) => {
      const status = error.response?.status
      // 仅 401/403（认证/授权失败）才强制登出
      // 500 等服务端错误视为系统故障，不登出
      if (status === 401 || status === 403) {
        clearTokens()
        notifySessionExpired()
        if (!isPublicPage()) {
          window.location.replace('/login')
        }
      }
      throw error
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

/**
 * 把刷新失败收敛成中文 ApiError（修复订单详情报错露英文）。
 *
 * 此前 401 分支把「导致本次刷新的那个原始 401 AxiosError」原样抛给调用方，其 message 是
 * axios 内置英文 `Request failed with status code 401`，而 store 层普遍用 `e.message` 当
 * 用户文案（如 stores/orderAdmin.ts），于是一次续期故障在界面上表现为一句英文。
 * 现在按失败原因分流：认证失败（401/403，executeRefresh 已登出）说「登录状态已过期」，
 * 网络/超时/5xx 说清是网络或服务器问题（此时会话并没有坏，不应误导用户重新登录）。
 */
function refreshFailureError(error: unknown): ApiError {
  const status =
    error && typeof error === 'object' && 'isAxiosError' in error
      ? (error as AxiosError).response?.status
      : undefined
  if (status === 401 || status === 403) {
    return new ApiError(4001, '登录状态已过期，请重新登录')
  }
  // -1 = 网络/HTTP 层错误（口径见 utils/apiError.ts），classifyError 已给出中文文案
  return new ApiError(-1, classifyError(error).userMessage)
}

/** 主动刷新 token（页面可见性恢复时调用） */
async function proactiveRefresh(): Promise<boolean> {
  try {
    await executeRefresh()
    return true
  } catch (e) {
    console.warn('[Token] Proactive refresh failed', e)
    return false
  }
}

/** 通知应用层执行完整登出，同步 reactive auth state */
function notifySessionExpired(): void {
  window.dispatchEvent(new CustomEvent('auth:session-expired'))
}

/** 判断当前是否为公开页面（无需登录） */
function isPublicPage(): boolean {
  const path = window.location.pathname
  const publicPaths = ['/', '/login']
  return publicPaths.includes(path) || path.startsWith('/api/public/')
}

const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器 - 自动注入 Token（公开 API 除外），access token 进入过期窗口时先续期
httpClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken()
    const url = config.url ?? ''
    const isPublicApi = url.startsWith('/api/public/')
    // 公开 API 不需要认证 Token，避免过期 token 导致 401 跳转登录页
    if (token && config.headers && !isPublicApi) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // FormData 请求让浏览器自动设置 Content-Type（含 boundary）
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type']
    }

    // 预续期：后台轮询（未读数/即将过期计数等）会一直带着同一个 access token 发请求，
    // 等它自然过期再靠 401 触发刷新，浏览器控制台每次都要留一条红色 401
    //（如 /api/system-messages/notifications/expiring-soon 401 Unauthorized），
    // 白白多一次往返。token 进入过期窗口时先换新再发，401 只留给会话真的失效的场景。
    // 认证端点排除在外：登录/发码/登出不该被旧会话的续期牵连；并发请求由
    // executeRefresh 的共享 Promise 合并为一次刷新。
    if (
      !isPublicApi &&
      token &&
      !url.startsWith('/api/auth/') &&
      getRefreshToken() &&
      shouldRefreshAccessToken()
    ) {
      try {
        const freshAccessToken = await executeRefresh()
        if (config.headers) {
          config.headers.Authorization = `Bearer ${freshAccessToken}`
        }
      } catch {
        // 续期失败不阻断本次请求：按原 token 发出，401 由响应拦截器统一收敛（重试或登出）
      }
    }
    return config
  },
  (error) => Promise.reject(error),
)

// 响应拦截器 - 全局解包 BackendResponse，code===200 取 data，否则抛 ApiError
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const raw = response.data
    // 判据只看 code：后端 ApiResponse 带 @JsonInclude(NON_NULL)，ApiResponse.fail(code, msg)
    // 与 ApiResponse.success(null) 序列化后都没有 data 键。曾用 'data' in raw 参与判定，使这批
    // 信封落入下方 else 被当成功透传，上层 wrapResponse 再标 code:200，业务失败就此伪装成数据
    // 写进 store（智能分析页即把 {code:404,message:"暂无分析结果"} 当成分析结果挂载后崩溃）。
    // typeof 守卫必须保留：'in' 作用于字符串会抛 TypeError，而 Blob/ArrayBuffer 虽为 object
    // 但无 code 键，正是要走 else 透传的裸响应（responseType: 'blob' 的导出/预览/下载）。
    if (raw && typeof raw === 'object' && 'code' in raw) {
      const wrapped = raw as unknown as ApiResponse<unknown>
      if (wrapped.code === 200) {
        response.data = wrapped.data
        // 成功响应：派发 http-success 事件，用于全局错误横幅自动清除
        window.dispatchEvent(new CustomEvent('http-success'))
      } else {
        const errCode: number = wrapped.code ?? -1
        throw new ApiError(errCode, wrapped.message || '请求失败')
      }
    } else {
      // 无 code 字段的裸响应（Blob/ArrayBuffer/字符串/数字/数组）视为成功透传
      window.dispatchEvent(new CustomEvent('http-success'))
    }
    return response
  },
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    const status = error.response?.status

    // ===== 处理 401：未认证 / Token 过期 =====
    if (status === 401) {
      // 没有 Refresh Token，或已是重试请求 → 直接登出
      if (originalRequest._retry || !getRefreshToken()) {
        clearTokens()
        notifySessionExpired()
        if (!isPublicPage()) {
          window.location.replace('/login')
        }
        // 同上：登出路径也不能把 axios 英文 message 透给只认 e.message 的调用方
        return Promise.reject(new ApiError(4001, '登录状态已过期，请重新登录'))
      }

      originalRequest._retry = true

      // 刷新失败与重放失败分开处理：此前一个 catch 兜住整条链，于是「刷新成功、重放请求
      // 自己失败（5xx/断网/业务码非 200）」也会被换成原始 401 抛出，既丢了真实原因，
      // 又把英文 message 泄露给只认 e.message 的调用方。重放请求的错误由它自己经拦截器分类。
      let accessToken: string
      try {
        accessToken = await executeRefresh()
      } catch (refreshError) {
        return Promise.reject(refreshFailureError(refreshError))
      }

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`
      }
      return httpClient(originalRequest)
    }

    // ===== 处理 403：个人空间限制派发事件给 CreateOrgPrompt =====
    // 不在这条分支提前 reject：403 与其他 4xx 一样走下面的统一收尾——classifyError 对 4xx
    // 既不广播全局横幅、也已提取后端中文 message，行为等价，少一条「以后在这里加处理会被
    // 前面那句 return 静默绕过」的分支。
    if (status === 403) {
      const errorData = error.response?.data as any
      const errorCode = errorData?.code
      if ((PERSONAL_SPACE_ERROR_CODES as readonly number[]).includes(errorCode)) {
        // Dispatch event for CreateOrgPrompt to show
        window.dispatchEvent(new CustomEvent('personal-space-limit', {
          detail: { code: errorCode, message: errorData?.message || '' }
        }))
      }
    }

    // ===== 处理注销冷静期拦截（HTTP 400 + 业务码 4000）=====
    // 后端 PendingDeletionFilter 对冷静期用户拦截全部业务 API（白名单除外）。
    // 引导到注销状态页（可撤销），而不是在每个页面反复弹错误 toast。
    if (status === 400 && (error.response?.data as any)?.code === 4000) {
      const errData = error.response?.data as any
      if (window.location.pathname !== '/settings/danger') {
        window.location.replace('/settings/danger')
      }
      return Promise.reject(new ApiError(4000, errData?.message || '您的账号已提交注销申请，处于冷静期中'))
    }

    // ===== 处理已注销账号（HTTP 400 + 业务码 4012）=====
    // 冷静期结束后存量的 access token 仍可能短暂有效，后端拦截后前端强制登出
    if (status === 400 && (error.response?.data as any)?.code === 4012) {
      const errData = error.response?.data as any
      clearTokens()
      notifySessionExpired()
      if (!isPublicPage()) {
        window.location.replace('/login')
      }
      return Promise.reject(new ApiError(4012, errData?.message || '该账号已注销'))
    }

    // ===== 广播全局系统错误（网络/超时/5xx）=====
    const classified = classifyError(error)
    if (classified.shouldShowGlobalAlert) {
      window.dispatchEvent(new CustomEvent('system-error', {
        detail: {
          category: classified.category,
          userMessage: classified.userMessage,
          status: classified.status,
          errorCode: classified.errorCode,
          shouldShowGlobalAlert: true,
        },
      }))
    }

    return Promise.reject(error)
  },
)

// 页面可见性恢复时，若 token 即将过期则主动刷新
// 注意：必须同时检查 refreshToken 是否存在，避免在公开页面（未登录）触发无意义的刷新
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && getRefreshToken() && isTokenExpiringSoon()) {
      proactiveRefresh().catch(() => {
        // 静默失败，不阻断用户操作；后续请求 401 时再走被动刷新
      })
    }
  })
}

/**
 * 4 个返回业务数据 T 的便捷方法（绕过 AxiosResponse 包装）。
 *
 * 拦截器内部已把 response.data 解包为后端 ApiResponse.data 字段（code===200 取 data），
 * 这里用 .then(r => r.data) 显式从 AxiosResponse 提取真正的 T。
 * 之前用 `as unknown as Promise<T>` 强转是错误的——axios 不会因为 R 泛型自动提取 .data，
 * 运行时仍返回 AxiosResponse 对象，导致调用方拿到 `{data, status, statusText, headers, config, request}`。
 */
const httpGet = <T>(url: string, config?: Parameters<AxiosInstance['get']>[1]): Promise<T> =>
  httpClient.get<T>(url, config).then((r) => r.data as T)
const httpPost = <T>(url: string, data?: unknown, config?: Parameters<AxiosInstance['post']>[2]): Promise<T> =>
  httpClient.post<T>(url, data, config).then((r) => r.data as T)
const httpPut = <T>(url: string, data?: unknown, config?: Parameters<AxiosInstance['put']>[2]): Promise<T> =>
  httpClient.put<T>(url, data, config).then((r) => r.data as T)
const httpDelete = <T>(url: string, config?: Parameters<AxiosInstance['delete']>[1]): Promise<T> =>
  httpClient.delete<T>(url, config).then((r) => r.data as T)
const httpPatch = <T>(url: string, data?: unknown, config?: Parameters<AxiosInstance['patch']>[2]): Promise<T> =>
  httpClient.patch<T>(url, data, config).then((r) => r.data as T)

export { httpClient, httpGet, httpPost, httpPut, httpDelete, httpPatch, API_BASE_URL, proactiveRefresh }
